import PropTypes from 'prop-types'
import React from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { connect } from 'react-redux'

import { toRoman } from 'helpers'
import { getEndDateMessage } from 'helpers/datetime'
import {
    getPeripheryById,
    isSortiesAvailable,
    isFortBattlesAvailable,
    getClosestMatchmakerTick,
} from 'helpers/peripheries'
import { joinToUnit } from 'state/actions/join_unit'
import { requestUnitParticipants } from 'state/actions/unit_participants'

import { Button, Tooltip } from 'react-wot-bootstrap'
import Clan from 'components/Clan/Clan'
import DefaultTooltip from 'components/DefaultTooltip/DefaultTooltip'
import {
    DisabledSlot,
    FreeSlot,
    LegionarySlot,
    MemberSlot,
} from './slots'

import { MAX_UNIT_PARTICIPANTS, UNIT_TYPES } from 'constants'
import {
    BATTLE_ROOMS_COLUMNS,
    STATUS_BATTLE_READY_MESSAGE,
    STATUS_IN_BATTLE_MESSAGE,
    STATUS_BATTLE_SCHEDULE_MESSAGE,
    STATUS_MATCHING_LEGIONARIES,
    BATTLE_MODE_DISABLED_ON_PERIPHERY,
} from '../../constants'
import {
    JOIN_BUTTON_CAPTION,
    TOOLTIPS,
} from './constants'

import { UnitDataPropTypes } from '../../propTypes'

import styles from './Squad.scss'

export class Squad extends React.Component {
    static propTypes = {
        forPreview: PropTypes.bool.isRequired,
        unitData: UnitDataPropTypes.isRequired,

        joinToUnit: PropTypes.func.isRequired,
        requestUnitParticipants: PropTypes.func.isRequired,
        meta: PropTypes.shape({
            sorties: PropTypes.object.isRequired,
            fort_battles: PropTypes.object.isRequired,
        }),
        participants: PropTypes.array.isRequired,
        realm: PropTypes.string.isRequired,
    }

    componentWillMount() {
        this.props.requestUnitParticipants()
    }

    componentDidUpdate(prevProps) {
        if (this.props.unitData.id !== prevProps.unitData.id) {
            this.props.requestUnitParticipants()
        }
    }

    // TODO: merge logic with UnitTooltip.prototype.getFooterMessage
    getFooterMessage() {
        let periphery
        let isAvailable
        let primetimeStart
        if (this.props.unitData.type === UNIT_TYPES.SORTIE) {
            periphery = getPeripheryById(this.props.meta.sorties.peripheries, this.props.unitData.periphery.id)
            isAvailable = isSortiesAvailable(periphery)
            primetimeStart = periphery ? periphery.primetime_start : null
        } else {
            periphery = getPeripheryById(this.props.meta.fort_battles.peripheries, this.props.unitData.periphery.id)
            isAvailable = isFortBattlesAvailable(periphery) || this.props.unitData.fort_battle_in_progress
            primetimeStart = periphery ? getClosestMatchmakerTick(periphery) : null
        }

        const highlightedClassName = classNames(styles.message, styles.message__highlighted)
        if (this.props.unitData.in_battle) {
            return (
                <p className={classNames(styles.message, styles.message__inBattle)}>
                    {STATUS_IN_BATTLE_MESSAGE}
                </p>
            )
        } else if (this.props.unitData.players_matching_started_at !== null) {
            return (
                <p className={highlightedClassName}>
                    {STATUS_MATCHING_LEGIONARIES}
                </p>
            )
        } else if (isAvailable) {
            return (
                <p className={styles.message}>
                    {STATUS_BATTLE_READY_MESSAGE}
                </p>
            )
        } else if (primetimeStart === null) {
            return this.props.unitData.fort_battle_in_progress ? null : (
                <p className={highlightedClassName}>
                    {BATTLE_MODE_DISABLED_ON_PERIPHERY}
                </p>
            )
        } else {
            const availableTime = getEndDateMessage(moment(primetimeStart))
            const timeNode = <span className={styles.scheduleTime}>{availableTime}</span>
            return (
                <p className={highlightedClassName}>
                    {STATUS_BATTLE_SCHEDULE_MESSAGE(timeNode)}
                </p>
            )
        }
    }

    renderTableHead() {
        const theadTRCLassName = classNames(styles.tr, styles.tr__thead)
        const vehicleTHClassName = classNames(styles.th, styles.th__vehicle)
        const nicknameTHClassName = classNames(styles.th, styles.th__nickname)
        const ratingTHClassName = classNames(styles.th, styles.th__rating)

        const playersColumnTH = this.props.forPreview ? (
            <div className={nicknameTHClassName} data-tip data-for={TOOLTIPS.PLAYERS.id}>
                {BATTLE_ROOMS_COLUMNS.squad}{': '}
                <span className={styles.count}>
                    {this.props.unitData.members_number}
                </span>
                {` / ${this.props.unitData.max_members_number}`}
                <DefaultTooltip {...TOOLTIPS.PLAYERS} />
            </div>
        ) : (
            <div className={nicknameTHClassName} data-tip data-for={TOOLTIPS.LEGIONARIES.id}>
                {BATTLE_ROOMS_COLUMNS.legionaries}{': '}
                <span className={styles.count}>
                    {this.props.unitData.legionaries_number}
                </span>
                {` / ${this.props.unitData.max_legionaries_number}`}
                <DefaultTooltip {...TOOLTIPS.LEGIONARIES} />
            </div>
        )

        return (
            <div className={theadTRCLassName}>
                {playersColumnTH}

                <div className={ratingTHClassName} data-tip data-for={TOOLTIPS.RATING.id}>
                    <div className={styles.ratingIcon}/>
                    <DefaultTooltip {...TOOLTIPS.RATING} />
                </div>

                <div className={vehicleTHClassName} data-tip data-for={TOOLTIPS.VEHICLE.id}>
                    {BATTLE_ROOMS_COLUMNS.vehicles}{': '}
                    <span className={styles.level}>
                        {toRoman(this.props.unitData.max_vehicle_level)}
                    </span>
                    <DefaultTooltip {...TOOLTIPS.VEHICLE} />
                </div>
            </div>
        )
    }

    renderSlots() {
        const { max_members_number, locked_slots_number } = this.props.unitData

        const memberSlots = this.props.participants.map((member, index) => (
            <MemberSlot
                index={index + 1}
                member={member}
                key={`member_slot_${index}`}
                realm={this.props.realm}
                isLegionary={member.clan_tag !== this.props.unitData.clan.tag}
            />
        ))

        const lockedSlots = Array.from(
            { length: locked_slots_number },
            (_, index) => <LegionarySlot key={`locked-slot-${index}`} />
        )

        const freeSlotsCount = max_members_number - this.props.participants.length - locked_slots_number
        const freeSlotsNumerationStart = this.props.participants.length + locked_slots_number

        const freeSlots = Array.from(
            { length: freeSlotsCount },
            (_, index) => {
                const slotIndex = freeSlotsNumerationStart + index + 1
                return (
                    <FreeSlot index={slotIndex} key={`free-slot-${index}`}/>
                )
            }
        )

        const disabledSlotsCount = MAX_UNIT_PARTICIPANTS - max_members_number
        const disabledSlots = Array.from(
            { length: disabledSlotsCount },
            (_, index) => <DisabledSlot index={index} key={`disabled-slot-${index}`}/>
        )

        return [
            ...memberSlots,
            ...lockedSlots,
            ...freeSlots,
            ...disabledSlots,
        ]
    }

    render() {
        let clan
        if (!this.props.forPreview) {
            clan = (
                <header data-tip data-for={TOOLTIPS.CLAN.id} className={styles.header}>
                    <Clan {...this.props.unitData.clan} />
                    <Tooltip id={TOOLTIPS.CLAN.id}>
                        <Clan {...this.props.unitData.clan} />
                    </Tooltip>
                </header>
            )
        }

        const squadClasses = classNames(styles.base, { [styles.unitPreview]: this.props.forPreview })
        return (
            <div className={squadClasses}>
                {clan}
                <main className={styles.table}>
                    <div className={styles.thead}>
                        {this.renderTableHead()}
                    </div>

                    <div className={styles.tbody}>
                        {this.renderSlots()}
                        <DefaultTooltip {...TOOLTIPS.NO_VEHICLE} />
                        <DefaultTooltip {...TOOLTIPS.LEGIONARY} />
                    </div>
                </main>

                <footer className={styles.footer}>
                    <div className={styles.button}>
                        <Button
                            onClick={this.props.joinToUnit}
                            tooltipId={TOOLTIPS.JOIN_BUTTON.id}
                        >
                            {JOIN_BUTTON_CAPTION}
                            <DefaultTooltip {...TOOLTIPS.JOIN_BUTTON} />
                        </Button>
                    </div>

                    {this.getFooterMessage()}
                </footer>
            </div>
        )
    }
}

const mapStateToProps = ({ settings, unitParticipants, availableUnits }) => {
    return {
        meta: availableUnits.meta,
        participants: unitParticipants.participants,
        realm: settings.realm,
    }
}

const mapDispatchToProps = (dispatch, ownProps) => {
    const unit = ownProps.unitData
    return {
        joinToUnit: () => dispatch(joinToUnit(unit.server_id, unit.periphery.id)),
        requestUnitParticipants: () => dispatch(requestUnitParticipants(unit.server_id, unit.periphery.id)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Squad)



// WEBPACK FOOTER //
// ./containers/BattleRooms/components/Squad/Squad.jsx